import { Tag, Space, Tooltip } from 'antd';
import { displayNumbers } from '../../../../variables/numbersFunctions';

// ── Regroupement par BRANCHE (A/B/T → TPV ; VC/VL/VT → VT ; sinon le code catégorie) ──
export const brancheOf = (code) => {
    if (['A', 'B', 'T'].includes(code)) return { code: 'TPV', nom: 'TPV' };
    if (['VC', 'VL', 'VT'].includes(code)) return { code: 'VT', nom: 'Véhicule de Transit (VT)' };
    return { code: code || '-', nom: code || '-' };
};

// Ré-agrège les lignes de la "Vue globale" (par catégorie) au niveau BRANCHE,
// en conservant exercice/mois/organisme/agence. Fusionne aussi le détail par producteur.
export const aggregateByBranche = (rows = []) => {
    const N = (v) => Number(v || 0);
    const map = new Map();
    rows.forEach((r) => {
        const b = brancheOf(r.categorieCode);
        const key = `${r.exercice}|${r.mois ?? ''}|${r.organismeCode ?? ''}|${r.agenceCode ?? ''}|${b.code}`;
        let acc = map.get(key);
        if (!acc) {
            acc = {
                exercice: r.exercice, mois: r.mois,
                organismeCode: r.organismeCode, organismeNom: r.organismeNom,
                agenceCode: r.agenceCode, agenceNom: r.agenceNom,
                categorieCode: b.code, categorieNom: b.nom,
                nombreContrats: 0, nombreAvenants: 0, nombreAssures: 0,
                nombreEmissions: 0, montantEmissions: 0, primeNetteEmissions: 0,
                nombreAnnulations: 0, montantAnnulations: 0, primeNetteAnnulations: 0,
                montantNet: 0, primeNetteNet: 0,
                _prod: new Map(),
            };
            map.set(key, acc);
        }
        acc.nombreContrats += N(r.nombreContrats);
        acc.nombreAvenants += N(r.nombreAvenants);
        acc.nombreAssures += N(r.nombreAssures);
        acc.nombreEmissions += N(r.nombreEmissions);
        acc.montantEmissions += N(r.montantEmissions);
        acc.primeNetteEmissions += N(r.primeNetteEmissions);
        acc.nombreAnnulations += N(r.nombreAnnulations);
        acc.montantAnnulations += N(r.montantAnnulations);
        acc.primeNetteAnnulations += N(r.primeNetteAnnulations);
        acc.montantNet += N(r.montantNet);
        acc.primeNetteNet += N(r.primeNetteNet);
        (r.detailParProducteur || []).forEach((p) => {
            const pk = p.producteurCode || p.producteurNom || '?';
            let pp = acc._prod.get(pk);
            if (!pp) {
                pp = { producteurCode: p.producteurCode, producteurNom: p.producteurNom, nombreContrats: 0, nombreEmissions: 0, montantEmissions: 0, nombreAnnulations: 0, montantAnnulations: 0, montantNet: 0 };
                acc._prod.set(pk, pp);
            }
            pp.nombreContrats += N(p.nombreContrats);
            pp.nombreEmissions += N(p.nombreEmissions);
            pp.montantEmissions += N(p.montantEmissions);
            pp.nombreAnnulations += N(p.nombreAnnulations);
            pp.montantAnnulations += N(p.montantAnnulations);
            pp.montantNet += N(p.montantNet);
        });
    });
    return Array.from(map.values()).map(({ _prod, ...rest }) => ({ ...rest, detailParProducteur: Array.from(_prod.values()) }));
};

export const statistiquesGlobalesColumns = () => [
    {
        title: 'Exercice',
        dataIndex: 'exercice',
        key: 'exercice',
        width: 100,
        fixed: 'left',
        sorter: (a, b) => a.exercice - b.exercice,
    },
    {
        title: 'Mois',
        dataIndex: 'mois',
        key: 'mois',
        width: 80,
        render: (mois) => {
            if (!mois) return '-';
            const moisNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
                'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
            return moisNames[mois - 1];
        }
    },
    {
        title: 'Organisme',
        dataIndex: 'organismeNom',
        key: 'organismeNom',
        width: 180,
        fixed: 'left',
        ellipsis: true,
    },
    {
        title: 'Agence',
        dataIndex: 'agenceNom',
        key: 'agenceNom',
        width: 150,
        ellipsis: true,
    },
    {
        title: 'Branche',
        dataIndex: 'categorieNom',
        key: 'categorieNom',
        width: 120,
        render: (text, record) => (
            <Tag color="geekblue">{record.categorieCode}</Tag>
        )
    },
    {
        title: 'Contrats',
        dataIndex: 'nombreContrats',
        key: 'nombreContrats',
        width: 100,
        align: 'center',
        sorter: (a, b) => a.nombreContrats - b.nombreContrats,
    },
    {
        title: 'Assurés',
        dataIndex: 'nombreAssures',
        key: 'nombreAssures',
        width: 100,
        align: 'center',
    },
    {
        title: 'Émissions',
        children: [
            {
                title: 'Nombre',
                dataIndex: 'nombreEmissions',
                key: 'nombreEmissions',
                width: 90,
                align: 'center',
                render: (val) => <Tag color="green">{val || 0}</Tag>
            },
            {
                title: 'Montant',
                dataIndex: 'montantEmissions',
                key: 'montantEmissions',
                width: 130,
                align: 'right',
                render: (val) => (
                    <span style={{ color: '#52c41a', fontWeight: 500 }}>
                        {displayNumbers(val)} F
                    </span>
                ),
                sorter: (a, b) => a.montantEmissions - b.montantEmissions,
            },
            {
                title: 'Prime Nette',
                dataIndex: 'primeNetteEmissions',
                key: 'primeNetteEmissions',
                width: 130,
                align: 'right',
                render: (val) => `${displayNumbers(val)} F`
            },
        ]
    },
    {
        title: 'Annulations',
        children: [
            {
                title: 'Nombre',
                dataIndex: 'nombreAnnulations',
                key: 'nombreAnnulations',
                width: 90,
                align: 'center',
                render: (val) => <Tag color="red">{val || 0}</Tag>
            },
            {
                title: 'Montant',
                dataIndex: 'montantAnnulations',
                key: 'montantAnnulations',
                width: 130,
                align: 'right',
                render: (val) => (
                    <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        {displayNumbers(val)} F
                    </span>
                ),
                sorter: (a, b) => a.montantAnnulations - b.montantAnnulations,
            },
            {
                title: 'Prime Nette',
                dataIndex: 'primeNetteAnnulations',
                key: 'primeNetteAnnulations',
                width: 130,
                align: 'right',
                render: (val) => `${displayNumbers(val)} F`
            },
        ]
    },
    {
        title: 'Production Nette',
        children: [
            {
                title: 'Montant Net',
                dataIndex: 'montantNet',
                key: 'montantNet',
                width: 150,
                align: 'right',
                render: (val) => (
                    <span style={{
                        color: val >= 0 ? '#1890ff' : '#ff4d4f',
                        fontWeight: 700,
                        fontSize: '14px'
                    }}>
                        {displayNumbers(val)} F
                    </span>
                ),
                sorter: (a, b) => a.montantNet - b.montantNet,
            },
            {
                title: 'Prime Nette',
                dataIndex: 'primeNetteNet',
                key: 'primeNetteNet',
                width: 130,
                align: 'right',
                render: (val) => (
                    <span style={{ fontWeight: 600 }}>
                        {displayNumbers(val)} F
                    </span>
                )
            },
        ]
    },
];

export const detailContratsColumns = () => [
    {
        title: 'N° Police',
        dataIndex: 'numeroPolice',
        key: 'numeroPolice',
        width: 150,
        fixed: 'left',
        ellipsis: true,
    },
    {
        title: 'Assuré',
        dataIndex: 'assureNom',
        key: 'assureNom',
        width: 200,
        ellipsis: true,
    },
    {
        title: 'Date Effet',
        dataIndex: 'dateEffet',
        key: 'dateEffet',
        width: 110,
        render: (date) => date ? new Date(date).toLocaleDateString('fr-FR') : '-'
    },
    {
        title: 'Organisme',
        dataIndex: 'organismeNom',
        key: 'organismeNom',
        width: 180,
        ellipsis: true,
    },
    {
        title: 'Agence',
        dataIndex: 'agenceNom',
        key: 'agenceNom',
        width: 150,
        ellipsis: true,
    },
    {
        title: 'Branche',
        dataIndex: 'categorieCode',
        key: 'categorieCode',
        width: 100,
        render: (text) => <Tag color="geekblue">{brancheOf(text).code}</Tag>
    },
    {
        title: 'Producteur',
        dataIndex: 'producteurNom',
        key: 'producteurNom',
        width: 180,
        ellipsis: true,
    },
    {
        title: 'Émissions',
        children: [
            {
                title: 'Nombre',
                dataIndex: 'nombreEmissions',
                key: 'nombreEmissions',
                width: 80,
                align: 'center',
                render: (val) => <Tag color="green">{val || 0}</Tag>
            },
            {
                title: 'Montant',
                dataIndex: 'montantEmission',
                key: 'montantEmission',
                width: 130,
                align: 'right',
                render: (val) => (
                    <span style={{ color: '#52c41a', fontWeight: 500 }}>
                        {displayNumbers(val)} F
                    </span>
                )
            },
        ]
    },
    {
        title: 'Annulations',
        children: [
            {
                title: 'Nombre',
                dataIndex: 'nombreAnnulations',
                key: 'nombreAnnulations',
                width: 80,
                align: 'center',
                render: (val) => <Tag color="red">{val || 0}</Tag>
            },
            {
                title: 'Montant',
                dataIndex: 'montantAnnulation',
                key: 'montantAnnulation',
                width: 130,
                align: 'right',
                render: (val) => (
                    <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        {displayNumbers(val)} F
                    </span>
                )
            },
        ]
    },
    {
        title: 'Montant Net',
        dataIndex: 'montantNet',
        key: 'montantNet',
        width: 150,
        align: 'right',
        fixed: 'right',
        render: (val) => (
            <span style={{
                color: val >= 0 ? '#1890ff' : '#ff4d4f',
                fontWeight: 700,
                fontSize: '14px'
            }}>
                {displayNumbers(val)} F
            </span>
        ),
        sorter: (a, b) => a.montantNet - b.montantNet,
    },
];